import React from 'react'
import { PEN_TOOL_CURSOR } from '@/components/app/editor/tools/pen-cursor-urls'
import { PenAnchorMarkers } from './components/pen-anchor-markers'
import { PenHandleLines } from './components/pen-handle-lines'
import { PenPathPreview } from './components/pen-path-preview'
import { buildCommittedPathD, buildRubberBandD } from './helpers/pen-path-d'
import { usePenDrawingState } from './hooks/use-pen-drawing-state'
import { usePenKeyboard } from './hooks/use-pen-keyboard'
import { usePenPointerListeners } from './hooks/use-pen-pointer-listeners'
import { usePenPropRefs } from './hooks/use-pen-prop-refs'
import type { PenToolOverlayHandle, PenToolOverlayProps } from './types/pen-tool-overlay-props'

export const PenToolOverlay = React.forwardRef<PenToolOverlayHandle, PenToolOverlayProps>(
  (
    {
      bleedWidth,
      bleedHeight,
      scale,
      defaultPointType,
      onPathCompleted,
      onCancel,
      canvasScrollerRef,
      onExitPenTool,
      initialPoints,
      skipShapeExitCheck = false,
      onDrawingStart,
      snapToGrid = false,
      gridSnapStep,
      gridSnapLiveRects,
      smartGuidesEnabled = false,
      snapGuideDistance = 8,
      getShapeBoundsForSmartGuides,
      onSmartGuidesChange,
    },
    ref
  ) => {
    const propRefs = usePenPropRefs({
      bleedWidth,
      bleedHeight,
      scale,
      defaultPointType,
      onPathCompleted,
      onCancel,
      canvasScrollerRef,
      onExitPenTool,
      skipShapeExitCheck,
      onDrawingStart,
      snapToGrid,
      gridSnapStep,
      gridSnapLiveRects,
      smartGuidesEnabled,
      snapGuideDistance,
      getShapeBoundsForSmartGuides,
      onSmartGuidesChange,
    })
    // Effect order: imperative handle (inside the drawing state) → pointer listeners → keyboard.
    const drawing = usePenDrawingState({
      initialPoints,
      onPathCompletedRef: propRefs.onPathCompletedRef,
      ref,
    })

    usePenPointerListeners(drawing, propRefs)
    usePenKeyboard(drawing)

    const { svgRef, mousePosState, isNearFirst } = drawing
    const points = drawing.pointsRef.current
    const lastPt = points.length > 0 ? points[points.length - 1] : null

    // Visual sizes in canvas units (appear constant in screen pixels at any zoom)
    const sw = 1 / scale
    const anchorHalf = 5.5 / scale // 11px anchor squares
    const handleR = 4 / scale // 8px handle circles
    const dashLen = 2 / scale // 2px dash segments

    const handleStrokeWidth = 1.5 / scale

    const committedD = buildCommittedPathD(points)
    const rubberBandD =
      mousePosState && lastPt && !isNearFirst
        ? buildRubberBandD(lastPt, mousePosState.x, mousePosState.y)
        : ''

    const cursorStyle = isNearFirst && points.length >= 2 ? 'pointer' : PEN_TOOL_CURSOR

    return (
      <svg
        ref={svgRef}
        className="absolute inset-0 select-none"
        style={{
          width: bleedWidth,
          height: bleedHeight,
          cursor: cursorStyle,
          overflow: 'visible',
          zIndex: 20,
        }}
      >
        {/* Transparent background rect ensures pointer events fire even over empty canvas */}
        <rect x={0} y={0} width={bleedWidth} height={bleedHeight} fill="transparent" />

        <PenPathPreview
          committedD={committedD}
          pointCount={points.length}
          rubberBandD={rubberBandD}
          sw={sw}
          dashLen={dashLen}
        />

        <PenHandleLines
          points={points}
          sw={sw}
          handleR={handleR}
          handleStrokeWidth={handleStrokeWidth}
        />

        <PenAnchorMarkers
          points={points}
          anchorHalf={anchorHalf}
          sw={sw}
          handleStrokeWidth={handleStrokeWidth}
          isNearFirst={isNearFirst}
        />
      </svg>
    )
  }
)

PenToolOverlay.displayName = 'PenToolOverlay'
